import TableHeader from "../rosterTable/TableHeader";
import { RosterShiftLegendChips } from "../rosterTable/RosterShiftLegendChips";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import TableBody from "../rosterTable/TableBody";
import "@css/modal_submittedWantedList.css";
import type * as type from "@vTypes";
import type { NurseResponse } from "@/types/nurse-type";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import RosterMadeTable from "../rosterTable/RosterMadeTable";
import { RosterViolationFloatingControls } from "../rosterTable/RosterViolationFloatingControls";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth.ts";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { useGroupStore } from "@store/groupStore.ts";
import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  type MouseEvent,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import CustomModal from "@/components/modal/CustomModal";
import ModalRosterModify from "./ModalRosterModify";
import TabButton from "@/components/common/TabButton";
import ActionButton from "@/components/common/ActionButton";
import {
  useWantedAdjustmentQuery,
  type WantedAdjustmentData,
  type WantedAdjustmentEntry,
  type WantedAdjustmentNurse,
} from "@/hooks/roster-craete/useWantedAdjustmentQuery";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import { useRosterValidation } from "@/hooks/useRosterValidation";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import { cn } from "@/lib/utils";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { resolveShiftFromWantedEntry } from "@/utils/wanted-shift-util";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { useRosterFocusStore } from "@/store/rosterFocusStore";

function LoadingOverlay({
  className,
  message,
}: {
  className?: string;
  message: string;
}) {
  return (
    <div
      className={cn(
        "absolute pointer-events-auto inset-0 z-[100] flex items-center justify-center bg-white/50",
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="bg-white p-6 px-10 rounded-lg shadow-2xl">
        <LoadingSpiner className="font-pretendard! text-base" msg={message} />
      </div>
    </div>
  );
}

function WantedSettingSwitchRow({
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 min-w-[200px] sm:min-w-0 sm:flex-1 sm:max-w-[240px]",
        disabled && "opacity-50"
      )}
    >
      <span className="text-sm! text-zinc-700 whitespace-nowrap shrink-0">
        {label}
      </span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-zinc-300"
        aria-label={label}
      />
    </div>
  );
}

function ModalRosterWanted({
  defaultShiftCode,
  currentScheduleInfo,
  holidayDates,
  nurses,
  shiftInfo,
  onClose,
  isViewOnlyOff,
  showManpowerButton,
  manPower_monthly,
  manPower_daily,
  memo,
  setMemo,
  isEditableMemo,
  currentScheduleID,
  prevTailData,
  initialTab = "view", // 초기 탭 설정
}: {
  defaultShiftCode: type.availableShifts[];
  currentScheduleInfo: { year: number; month: number; version: number };
  holidayDates: string[] | null;
  nurses: NurseResponse[] | null;
  shiftInfo: type.availableShifts[];
  onClose: () => void;
  isViewOnlyOff: boolean;
  showManpowerButton: boolean;
  manPower_monthly: type.ManPowerMonthly | null;
  manPower_daily?: type.ManPowerDaily | null;
  memo: string;
  setMemo?: React.Dispatch<React.SetStateAction<string>>;
  isEditableMemo: boolean;
  currentScheduleID: string | undefined;
  prevTailData?: PrevTailData | null;
  initialTab?: "view" | "settings"; // 초기 탭 설정
}) {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const loginUser = useLoginUser();
  const { selectedGroupId } = useGroupStore();
  const isDragEditing = false;

  const gid = isAdmin ? (selectedGroupId ?? "") : "";
  const { data: shiftsData } = useShiftsQuery(gid || undefined);
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    defaultShiftCode,
    shiftsData ?? null
  );

  // 모달이 닫힐 때 진행 중인 wanted/adjustment 쿼리 취소
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 쿼리 취소
      if (currentScheduleInfo?.year && currentScheduleInfo?.month) {
        queryClient.cancelQueries({
          queryKey: QUERY_KEYS.wanted.adjustment(
            currentScheduleInfo.year,
            currentScheduleInfo.month
          ),
        });
      }
    };
  }, [queryClient, currentScheduleInfo?.year, currentScheduleInfo?.month]);

  // 탭 상태 관리
  const [activeTab, setActiveTab] = useState<"view" | "settings">(initialTab);

  // 초기 데이터 저장 (초기화용)
  const [initialWantedAdjustmentData, setInitialWantedAdjustmentData] =
    useState<WantedAdjustmentData | null>(null);

  // 로컬에서 변경된 entries 관리 (nurse_id + shift_date를 키로 사용)
  const [modifiedEntries, setModifiedEntries] = useState<
    Map<string, WantedAdjustmentEntry>
  >(new Map());

  // 설정완료 확인 모달 상태
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRosterMadeSummaryMinimized, setIsRosterMadeSummaryMinimized] =
    useState(false);
  const [showPerShiftStats, setShowPerShiftStats] = useState(false);

  // 닫기 확인 모달 상태
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);

  // 근무코드 추가 모달 상태
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

  // 원티드 전체 반영/미반영 토글 상태
  const [isAllApplied, setIsAllApplied] = useState(true);

  // 조정판 전용 위반사항 표시 상태 (로컬 상태, 전역 상태와 분리)
  const [localShowManpowerViolations, setLocalShowManpowerViolations] =
    useState(true);
  const [localShowConfigViolations, setLocalShowConfigViolations] =
    useState(true);
  const [localShowWantedViolations, setLocalShowWantedViolations] =
    useState(false); // 조정판에서는 원티드 반영사항 보기 불필요

  // 선택된 근무코드 상태
  const [selectedShift, setSelectedShift] =
    useState<type.Type_availableShifts | null>(null);

  // wanted/adjustment API 호출 (스냅샷 데이터)
  const { data: wantedAdjustmentData, isLoading: isWantedAdjustmentLoading } =
    useWantedAdjustmentQuery(
      currentScheduleInfo.year,
      currentScheduleInfo.month
    );


  const wantedAssignmentsByNurse = useMemo(() => {
    const map = new Map<string, InboundItem[]>();
    const rows = wantedAdjustmentData?.assignments ?? [];
    for (const a of rows) {
      const nurseId = String(a.nurse_id ?? "").trim();
      if (nurseId === "") continue;
      const startDate = a.start_date ?? a.startDate ?? "";
      if (startDate === "") continue;
      const prev = map.get(nurseId) ?? [];
      prev.push({
        startDate,
        endDate: a.end_date ?? a.endDate ?? null,
        reason: a.reason,
        target_group_id: a.target_group_id ?? a.targetGroupId,
        target_group_name: a.target_group_name ?? a.targetGroupName,
      });
      map.set(nurseId, prev);
    }
    return map;
  }, [wantedAdjustmentData?.assignments]);

  // 초기 데이터 저장 (deep copy)
  useEffect(() => {
    if (wantedAdjustmentData && !initialWantedAdjustmentData) {
      const deepCopy = JSON.parse(
        JSON.stringify(wantedAdjustmentData)
      ) as WantedAdjustmentData;
      setInitialWantedAdjustmentData(deepCopy);
    }
  }, [wantedAdjustmentData, initialWantedAdjustmentData]);

  // 탭이 "view"로 변경되면 근무코드 추가 모달 닫기
  useEffect(() => {
    if (activeTab === "view") {
      setIsModifyModalOpen(false);
    }
  }, [activeTab]);

  /** 설정 탭은 클릭 선택 없음 — 현황에서 남은 열·행 선택 잔상 제거 */
  useEffect(() => {
    if (activeTab === "settings") {
      useRosterFocusStore.getState().clearSelectedFocus();
    }
  }, [activeTab]);

  useEffect(() => {
    setShowPerShiftStats(false);
  }, [currentScheduleInfo.year, currentScheduleInfo.month, currentScheduleID]);

  // wantedAdjustmentData를 copyNurses 형태로 변환 (원본 순서·내용)
  const wantedCopyNursesComputed = useMemo(() => {
    if (!nurses || nurses.length === 0) {
      return [];
    }

    // active === 1인 인원만 필터링
    const activeNurses = nurses.filter((nurse) => nurse.active === 1);
    if (activeNurses.length === 0) {
      return [];
    }

    // 로딩 중이거나 wantedAdjustmentData가 없으면 빈 스케줄로 표시 (간호사 목록은 먼저 보여줌)
    if (isWantedAdjustmentLoading || !wantedAdjustmentData?.nurses) {
      const daysInMonth = new Date(
        currentScheduleInfo.year,
        currentScheduleInfo.month,
        0
      ).getDate();

      const emptyCounts: Record<string, number> = {};
      defaultShiftCode.forEach((s) => {
        if (s.shift_id) emptyCounts[s.shift_id] = 0;
      });

      return sortNurseListByRoleThenSequence(
        activeNurses.map((nurse) => ({
          ...nurse,
          id: nurse.nurse_id, // id 필드 추가
          off: nurse.personal_off_adjustment ?? 0, // off 필드 추가
          level: nurse.level_ ?? null, // level 필드 추가
          schedule: Array(daysInMonth).fill(null),
          schedule_ids: Array(daysInMonth).fill(null),
          counts: emptyCounts,
          shiftMessages: {},
        }))
      );
    }

    const daysInMonth = new Date(
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      0
    ).getDate();

    // wantedAdjustmentData.nurses를 Map으로 변환 (빠른 조회를 위해)
    const wantedNursesMap = new Map(
      wantedAdjustmentData.nurses.map((wantedNurse) => [
        wantedNurse.nurse_id,
        wantedNurse,
      ])
    );

    const rows = activeNurses.map((nurse) => {
      const wantedNurse: WantedAdjustmentNurse | undefined =
        wantedNursesMap.get(nurse.nurse_id);

      // schedule 배열 초기화 (원티드 데이터만 표시)
      const schedule: (string | null)[] = Array(daysInMonth).fill(null);
      const scheduleIds: (number | null)[] = Array(daysInMonth).fill(null);
      const shiftMessages: Record<number, string> = {};
      const isAppliedMap: Record<number, boolean> = {}; // 날짜별 is_applied 정보

      if (wantedNurse?.entries) {
        // entries를 파싱해서 schedule 배열 생성
        wantedNurse.entries.forEach((entry: WantedAdjustmentEntry) => {
          if (entry.shift_date && entry.shift_id) {
            // shift_date에서 날짜 추출 (예: "2026-03-15" -> 15)
            const date = new Date(entry.shift_date);
            const day = date.getDate();

            // 유효한 날짜인지 확인 (1 ~ daysInMonth)
            if (day >= 1 && day <= daysInMonth) {
              const dayIndex = day - 1; // 0-based index

              // modifiedEntries에서 변경된 entry 확인
              const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
              const modifiedEntry = modifiedEntries.get(entryKey);
              const currentEntry = modifiedEntry || entry;
              const isApplied = currentEntry.is_applied;

              const tableId =
                currentEntry.shifts_table_id != null &&
                Number.isFinite(Number(currentEntry.shifts_table_id))
                  ? Number(currentEntry.shifts_table_id)
                  : null;

              // is_applied 정보 저장 (X 아이콘 표시용)
              isAppliedMap[day] = isApplied;

              // is_applied가 false여도 표시 (X 아이콘으로 표시)
              // isViewOnlyOff 모드면 O만 표시, 아니면 모든 shift 표시
              if (isViewOnlyOff) {
                if (currentEntry.shift_id === "O") {
                  schedule[dayIndex] = "O";
                  scheduleIds[dayIndex] = tableId;
                }
              } else {
                schedule[dayIndex] = currentEntry.shift_id;
                scheduleIds[dayIndex] = tableId;
              }

              // reason이 있으면 shiftMessages에 추가
              if (currentEntry.reason) {
                shiftMessages[day] = currentEntry.reason;
              }
            }
          }
        });
      }

      // // counts 계산: schedule 기준으로 is_applied=true인 항목만 집계
      // // isAppliedMap을 확인하여 실시간으로 재계산
      // const counts = {
      //   D: 0,
      //   E: 0,
      //   N: 0,
      //   O: 0,
      // };

      // // schedule을 순회하면서 is_applied=true인 항목만 집계
      // schedule.forEach((shift, dayIndex) => {
      //   if (shift) {
      //     const day = dayIndex + 1; // 1-based
      //     const isApplied = isAppliedMap[day] ?? true; // 기본값은 true

      //     // is_applied=true인 항목만 집계
      //     if (isApplied) {
      //       if (shift === "D") counts.D++;
      //       else if (shift === "E") counts.E++;
      //       else if (shift === "N") counts.N++;
      //       else if (shift === "O") counts.O++;
      //     }
      //   }
      // });

      // 2. modifiedEntries에만 있는 새로운 항목 처리
      modifiedEntries.forEach((modifiedEntry, entryKey) => {
        const [modifiedNurseId] = entryKey.split("_");

        if (modifiedNurseId === nurse.nurse_id) {
          const date = new Date(modifiedEntry.shift_date);
          const day = date.getDate();

          if (day >= 1 && day <= daysInMonth) {
            const dayIndex = day - 1;

            const tableId =
              modifiedEntry.shifts_table_id != null &&
              Number.isFinite(Number(modifiedEntry.shifts_table_id))
                ? Number(modifiedEntry.shifts_table_id)
                : null;

            // schedule 업데이트
            if (isViewOnlyOff) {
              if (modifiedEntry.shift_id === "O") {
                schedule[dayIndex] = "O";
                scheduleIds[dayIndex] = tableId;
              }
            } else {
              schedule[dayIndex] = modifiedEntry.shift_id;
              scheduleIds[dayIndex] = tableId;
            }

            // is_applied 정보 저장
            isAppliedMap[day] = modifiedEntry.is_applied;

            // reason이 있으면 추가
            if (modifiedEntry.reason) {
              shiftMessages[day] = modifiedEntry.reason;
            }
          }
        }
      });

      // counts 계산: schedule 기준으로 is_applied=true인 항목만 집계
      // isAppliedMap을 확인하여 실시간으로 재계산
      const counts: Record<string, number> = {};
      defaultShiftCode.forEach((s) => {
        if (s.shift_id) counts[s.shift_id] = 0;
      });

      // schedule을 순회하면서 is_applied=true인 항목만 집계
      schedule.forEach((shift, dayIndex) => {
        if (shift) {
          const day = dayIndex + 1; // 1-based
          const isApplied = isAppliedMap[day] ?? true; // 기본값은 true

          // is_applied=true인 항목만 집계
          if (isApplied) {
            const countKey =
              resolveShiftFromWantedEntry(
                {
                  shift_id: shift,
                  shifts_table_id: scheduleIds[dayIndex],
                },
                shiftLookupMaps
              )?.shift_id ?? shift;
            counts[countKey] = (counts[countKey] || 0) + 1;
          }
        }
      });

      return {
        ...nurse,
        id: nurse.nurse_id, // id 필드 추가 (row_NurseRosterList에서 사용)
        off: nurse.personal_off_adjustment ?? 0, // off 필드 추가
        level: nurse.level_ ?? null, // level 필드 추가
        schedule,
        schedule_ids: scheduleIds,
        counts,
        shiftMessages,
        // 원티드 반영 설정용: entries 정보 추가
        wantedEntries: wantedNurse?.entries || [],
        // 원티드 반영 설정용: 날짜별 is_applied 정보 (X 아이콘 표시용)
        isAppliedMap: isAppliedMap,
        // 원티드 요청 API의 assignment 구간(파견/휴직 등) — 조정판 잠금/툴팁 기준
        assignments:
          wantedAssignmentsByNurse.get(String(nurse.nurse_id).trim()) ??
          // wanted 쿼리 비어있는 경우 기존 roster nurse의 배정 정보 fallback
          (nurse as unknown as { assignments?: InboundItem[] }).assignments ??
          [],
      };
    });
    return sortNurseListByRoleThenSequence(rows);
  }, [
    nurses,
    wantedAdjustmentData,
    currentScheduleInfo.year,
    currentScheduleInfo.month,
    isViewOnlyOff,
    modifiedEntries,
    defaultShiftCode,
    isWantedAdjustmentLoading, // 로딩 상태 추가
    shiftLookupMaps,
    wantedAssignmentsByNurse,
  ]);

  /** 헤더 정렬(이름/G/경력) 반영 — API·수정으로 computed가 갱신될 때 동일 인원이면 이전 행 순서 유지 */
  const [wantedCopyNurses, setWantedCopyNurses] = useState(
    wantedCopyNursesComputed
  );

  useEffect(() => {
    const next = wantedCopyNursesComputed;
    setWantedCopyNurses((prev) => {
      if (prev.length === 0) return next;
      const prevIds = prev.map((n) => String(n.nurse_id));
      const nextById = new Map(
        next.map((n) => [String(n.nurse_id), n] as const)
      );
      if (prevIds.length !== next.length) return next;
      if (!prevIds.every((id) => nextById.has(id))) return next;
      return prevIds.map((id) => nextById.get(id)!);
    });
  }, [wantedCopyNursesComputed]);

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  // 검증용 copyNurses: 프리셉티 제외 + isAppliedMap=false(거절된 원티드) 투명화
  const wantedPrecepteePolicy = getPrecepteePolicy(serverConfig);
  const validationCopyNurses = useMemo(() => {
    return (
      wantedCopyNurses
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((nurse: any) =>
          wantedPrecepteePolicy.excludePrecepteeFromCount
            ? nurse.preceptor?.role !== "preceptee"
            : true
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((nurse: any) => {
          if (!nurse.isAppliedMap) return nurse;
          return {
            ...nurse,
            schedule:
              nurse.schedule?.map((shift: string | null, idx: number) => {
                const day = idx + 1;
                if (nurse.isAppliedMap[day] === false) return null;
                return shift;
              }) ?? null,
          };
        })
    );
  }, [wantedCopyNurses, wantedPrecepteePolicy.excludePrecepteeFromCount]);

  // RosterMadeTable 통계용 전처리: 프리셉티 제외 + M 필터
  const wantedStatsNurses = useMemo(
    () =>
      wantedPrecepteePolicy.excludePrecepteeFromCount
        ? wantedCopyNurses.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (n: any) => n.preceptor?.role !== "preceptee"
          )
        : wantedCopyNurses,
    [wantedCopyNurses, wantedPrecepteePolicy.excludePrecepteeFromCount]
  );
  const wantedStatsShiftCode = useMemo(
    () =>
      isUseMid === false
        ? defaultShiftCode.filter((s) => s.default_shift !== "M")
        : defaultShiftCode,
    [defaultShiftCode, isUseMid]
  );

  // 원티드 조정판 전용 검증 (최소인원 검증 비활성화)
  const {
    validateList: rawWantedValidateList,
    isValidating: isWantedValidating,
    phase: wantedValidationPhase,
    validate: triggerWantedValidation,
  } = useRosterValidation({
    currentScheduleInfo,
    currentScheduleID,
    copyNurses: validationCopyNurses as Parameters<
      typeof useRosterValidation
    >[0]["copyNurses"],
    isDragEditing,
    manPower_monthly: isWantedAdjustmentLoading ? null : manPower_monthly,
    year: currentScheduleInfo.year,
    month: currentScheduleInfo.month,
    serverConfig: serverConfig ?? null,
    defaultShiftCode,
    shiftsData: shiftsData ?? [],
    disableManpowerValidation: true,
    prevTailData,
  });

  useEffect(() => {
    if (!isDragEditing && !isWantedAdjustmentLoading) triggerWantedValidation();
  }, [
    validationCopyNurses,
    currentScheduleID,
    isDragEditing,
    isWantedAdjustmentLoading,
    triggerWantedValidation,
  ]);

  const { displayShifts: wantedModalShiftLegend } =
    useRosterDisplayShiftColumns(
      wantedCopyNurses as {
        schedule?: (string | null)[] | null;
        schedule_ids?: (number | null)[] | null;
      }[],
      defaultShiftCode,
      isUseMid,
      { includeBaselineMainTemplateColumns: true }
    );

  // 원티드 반영 설정: 셀 클릭 핸들러 (is_applied 토글)
  const handleWantedCellClick = useCallback(
    (nurseId: string, day: number, shiftId?: string) => {
      if (activeTab !== "settings") {
        return;
      }

      const daysInMonth = new Date(
        currentScheduleInfo.year,
        currentScheduleInfo.month,
        0
      ).getDate();

      if (day < 1 || day > daysInMonth) {
        return;
      }

      // 해당 간호사의 wantedNurse 찾기
      const wantedNurse = wantedAdjustmentData?.nurses.find(
        (n) => n.nurse_id === nurseId
      );

      // 날짜 형식: YYYY-MM-DD
      const year = currentScheduleInfo.year;
      const month = String(currentScheduleInfo.month).padStart(2, "0");
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${year}-${month}-${dayStr}`;

      // entryKey 생성
      const entryKey = `${nurseId}_${dateStr}`;

      // 근무코드가 선택된 경우: 근무코드 추가/변경 모드
      if (selectedShift) {
        const existingEntry = wantedNurse?.entries.find(
          (e) => e.shift_date === dateStr
        );

        const pickedShiftRow = shiftInfo.find(
          (s) => s.shift_id === selectedShift.shift_id
        );
        const shiftsTableIdFromPicker =
          pickedShiftRow?.id != null &&
          Number.isFinite(Number(pickedShiftRow.id))
            ? Number(pickedShiftRow.id)
            : 0;

        const newEntry: WantedAdjustmentEntry = existingEntry
          ? {
              ...existingEntry,
              shift_id: selectedShift.shift_id,
              shifts_table_id:
                shiftsTableIdFromPicker || existingEntry.shifts_table_id,
              is_applied: true,
            }
          : {
              id: Date.now(),
              group_id: selectedGroupId || "",
              year: currentScheduleInfo.year,
              month: currentScheduleInfo.month,
              nurse_id: nurseId,
              shift_date: dateStr,
              shift_id: selectedShift.shift_id,
              shifts_table_id: shiftsTableIdFromPicker,
              is_applied: true,
              source_type: "manual",
              original_shift_id: null,
              reason: null,
              head_nurse_memo: null,
              created_by: null,
            };

        setModifiedEntries((prev) => {
          const newMap = new Map(prev);
          newMap.set(entryKey, newEntry);
          return newMap;
        });
        return;
      }

      // 주휴: 반영(is_applied) 토글 불가 (스케줄 칸 코드 기준)
      if (shiftId === "주") {
        return;
      }

      // 🆕 is_applied 토글 모드
      // 1. modifiedEntries에 있는지 먼저 확인
      const modifiedEntry = modifiedEntries.get(entryKey);

      if (modifiedEntry) {
        if (modifiedEntry.shift_id === "주") {
          return;
        }
        // modifiedEntries에 있으면 그것을 토글
        const newEntry: WantedAdjustmentEntry = {
          ...modifiedEntry,
          is_applied: !modifiedEntry.is_applied,
        };

        setModifiedEntries((prev) => {
          const newMap = new Map(prev);
          newMap.set(entryKey, newEntry);
          return newMap;
        });
        return;
      }

      if (!wantedNurse) {
        return;
      }

      // shiftId가 있으면 shift_id도 매칭, 없으면 날짜만 매칭
      const entry = wantedNurse.entries.find((e) => {
        const dateMatch = e.shift_date === dateStr;
        if (shiftId) {
          return dateMatch && e.shift_id === shiftId;
        }
        return dateMatch;
      });

      if (!entry) {
        return; // 해당 날짜에 원티드가 없으면 무시
      }

      if (entry.shift_id === "주") {
        return;
      }

      // modifiedEntries에서 현재 상태 확인
      const currentEntry = modifiedEntries.get(entryKey) || entry;

      // is_applied 토글
      const newEntry: WantedAdjustmentEntry = {
        ...currentEntry,
        is_applied: !currentEntry.is_applied,
      };

      // modifiedEntries 업데이트
      setModifiedEntries((prev) => {
        const newMap = new Map(prev);
        newMap.set(entryKey, newEntry);
        return newMap;
      });
    },
    [
      activeTab,
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      wantedAdjustmentData,
      modifiedEntries,
      selectedShift,
      selectedGroupId,
      shiftInfo,
    ]
  );

  // 원티드 전체 반영/미반영 토글 핸들러
  const handleToggleAllApplied = useCallback(() => {
    const newAppliedState = !isAllApplied;
    const newModifiedEntries = new Map(modifiedEntries);

    // 1. wantedAdjustmentData에 있는 entries 처리
    if (wantedAdjustmentData?.nurses) {
      wantedAdjustmentData.nurses.forEach((nurse) => {
        nurse.entries.forEach((entry) => {
          // 주휴 코드 제외
          if (entry.shift_id === "주") return;

          const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
          const currentEntry = modifiedEntries.get(entryKey) || entry;

          newModifiedEntries.set(entryKey, {
            ...currentEntry,
            is_applied: newAppliedState,
          });
        });
      });
    }

    // 2. modifiedEntries에만 있는 새로운 항목도 처리
    modifiedEntries.forEach((entry, entryKey) => {
      // 주휴 코드 제외
      if (entry.shift_id === "주") return;

      newModifiedEntries.set(entryKey, {
        ...entry,
        is_applied: newAppliedState,
      });
    });

    setModifiedEntries(newModifiedEntries);
    setIsAllApplied(newAppliedState);

    toast.success(
      newAppliedState
        ? "모든 원티드가 반영되었습니다."
        : "모든 원티드가 미반영되었습니다."
    );
  }, [wantedAdjustmentData, modifiedEntries, isAllApplied]);

  // 근무코드 추가하기 모달 > 전체 지우기 핸들러 (화면상의 변경사항만 취소)
  const deleteAllShift = useCallback(() => {
    if (modifiedEntries.size === 0) return;

    if (!confirm("입력된 변경사항을 모두 지우시겠습니까?")) {
      return;
    }

    // modifiedEntries 초기화
    setModifiedEntries(new Map());
    toast.success("입력된 내용이 초기화 되었습니다.");
  }, [modifiedEntries]);

  const getUpdatedWantedAdjustmentData =
    useCallback((): WantedAdjustmentData | null => {
      if (!wantedAdjustmentData) {
        return null;
      }

      // deep copy 생성
      const updatedData: WantedAdjustmentData = JSON.parse(
        JSON.stringify(wantedAdjustmentData)
      );

      // modifiedEntries의 변경사항을 각 nurse의 entries에 반영
      updatedData.nurses.forEach((nurse) => {
        nurse.entries = nurse.entries.map((entry) => {
          const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
          const modifiedEntry = modifiedEntries.get(entryKey);
          return modifiedEntry || entry;
        });
      });

      // 2. 새로 추가된 항목 처리
      modifiedEntries.forEach((modifiedEntry, entryKey) => {
        const [nurseId] = entryKey.split("_");
        const nurse = updatedData.nurses.find((n) => n.nurse_id === nurseId);

        if (nurse) {
          const exists = nurse.entries.some(
            (e) => e.shift_date === modifiedEntry.shift_date
          );
          if (!exists) nurse.entries.push(modifiedEntry);
        } else {
          updatedData.nurses.push({
            nurse_id: nurseId,
            name: nurses?.find((n) => n.nurse_id === nurseId)?.name || "",
            entries: [modifiedEntry],
            monthly_summary: {},
          });
        }
      });

      return updatedData;
    }, [wantedAdjustmentData, modifiedEntries, nurses]);

  // 리셋로직
  const {
    mutate: resetWantedAdjustment,
    isPending: isResettingWantedAdjustment,
  } = useMutation({
    mutationFn: async () => {
      if (!currentScheduleInfo.year || !currentScheduleInfo.month) {
        throw new Error("필수 항목이 누락 되었습니다.");
      }

      const response = await httpClient.post<WantedAdjustmentData>(
        `/wanted/adjustment/${currentScheduleInfo.year}/${currentScheduleInfo.month}/reset`
      );
      return response;
    },
    onSuccess: (data) => {
      // mutation 리턴값으로 쿼리 캐시 업데이트
      queryClient.setQueryData(
        QUERY_KEYS.wanted.adjustment(
          currentScheduleInfo.year,
          currentScheduleInfo.month
        ),
        data
      );

      // 초기 데이터도 업데이트
      const deepCopy = JSON.parse(JSON.stringify(data)) as WantedAdjustmentData;
      setInitialWantedAdjustmentData(deepCopy);

      // modifiedEntries 초기화
      setModifiedEntries(new Map());
      setIsAllApplied(true);

      toast.success("원티드 반영 설정이 초기화 되었습니다.");
    },
    onError: (error) => {
      console.error("원티드 반영 설정 초기화 실패:", error);
      toast.error("원티드 반영 설정 초기화에 실패했습니다.");
    },
  });

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    if (!confirm("초기화 하시겠습니까?")) {
      return;
    }
    resetWantedAdjustment();
  }, [resetWantedAdjustment]);

  // 닫기 핸들러: 변경사항이 있으면 확인 모달 표시
  const handleClose = useCallback(() => {
    // 진행 중인 wanted/adjustment 쿼리 취소
    if (currentScheduleInfo?.year && currentScheduleInfo?.month) {
      queryClient.cancelQueries({
        queryKey: QUERY_KEYS.wanted.adjustment(
          currentScheduleInfo.year,
          currentScheduleInfo.month
        ),
      });
    }

    // 변경사항이 있으면 확인 모달 표시
    if (modifiedEntries.size > 0) {
      setIsCloseConfirmModalOpen(true);
    } else {
      // 변경사항이 없으면 바로 닫기
      onClose();
    }
  }, [
    modifiedEntries.size,
    onClose,
    queryClient,
    currentScheduleInfo?.year,
    currentScheduleInfo?.month,
  ]);

  const {
    mutate: updateWantedAdjustment,
    isPending: isUpdatingWantedAdjustment,
  } = useMutation({
    mutationFn: async () => {
      const updatedData = getUpdatedWantedAdjustmentData();

      if (!updatedData) {
        throw new Error("원티드 데이터가 없습니다.");
      }

      const entries: Array<{
        nurse_id: string;
        shift_date: string;
        shift_id: string;
        shifts_table_id: number;
        is_applied: boolean;
        reason: string | null;
        head_nurse_memo: string | null;
      }> = [];

      updatedData.nurses.forEach((nurse) => {
        nurse.entries.forEach((entry) => {
          entries.push({
            nurse_id: entry.nurse_id,
            shift_date: entry.shift_date,
            shift_id: entry.shift_id,
            shifts_table_id: entry.shifts_table_id,
            is_applied: entry.is_applied,
            reason: entry.reason,
            head_nurse_memo: entry.head_nurse_memo,
          });
        });
      });

      const postData = {
        year: currentScheduleInfo.year,
        month: currentScheduleInfo.month,
        entries: entries,
      };

      // throw new Error("error")
      return await httpClient.post("/wanted/adjustment", postData);
    },
    onSuccess: () => {
      toast.success("원티드 반영 설정이 저장되었습니다.");
      // 저장 후 modifiedEntries 초기화
      setModifiedEntries(new Map());
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.adjustment(
          currentScheduleInfo.year,
          currentScheduleInfo.month
        ),
      });
    },
    onError: (error) => {
      console.error("원티드 반영 설정 저장 실패:", error);
      toast.error("원티드 반영 설정 저장에 실패했습니다.");
    },
  });

  const wantedLoadingOverlayMessage = isWantedAdjustmentLoading
    ? "전체 근무자 원티드 현황을 가져오는 중입니다."
    : isUpdatingWantedAdjustment
      ? "원티드 반영 설정을 저장하는 중입니다."
      : isResettingWantedAdjustment
        ? "원티드 반영 설정을 초기화하는 중입니다."
        : null;

  /** 모달이 `stopPropagation`으로 버블을 끊어 `TableBody_base`의 window 캡처와 겹치지 않는 경우 대비 */
  const clearRosterTableCellFocusIfOutside = useCallback((e: MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (
      t &&
      !t.closest("[data-roster-shift-cell]") &&
      !t.closest("[data-roster-header-day]")
    ) {
      useRosterFocusStore.getState().clearSelectedFocus();
    }
  }, []);

  return (
    <CustomModal.Confirm
      isOpen={true}
      onClose={handleClose}
      showActions={false}
      className=""
      contentClassName={cn(
        "us-n max-h-[calc(100vh-120px)] max-w-[calc(100vw-300px)]! flex flex-col min-h-0  p-0",
        wantedLoadingOverlayMessage && "overflow-hidden"
      )}
    >
      {wantedLoadingOverlayMessage && (
        <LoadingOverlay message={wantedLoadingOverlayMessage} />
      )}
      <div
        className="relative flex min-h-0 flex-1 flex-col "
        onClick={(e) => {
          clearRosterTableCellFocusIfOutside(e);
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          clearRosterTableCellFocusIfOutside(e);
          e.stopPropagation();
        }}
      >
        {/* 스크롤은 내부 영역만 — 로딩 오버레이가 스크롤에 따라 위로 밀리지 않음 */}
        <div className="min-h-0 flex-1  scrollbar-float">
          {/* 모달 컨텐츠 — 상단 헤더/통계 그룹은 Roster_create.tsx (989–1161)와 동일 DOM·클래스 구조 */}
          <div className="bg-white flex flex-col pt-0 pb-5">
            <div className="flex w-full sticky top-0 z-20 bg-white flex-nowrap px-4 justify-between gap-2 border-b pt-1">
              <div className="flex min-w-0 flex-nowrap gap-1">
                <TabButton
                  label="전체 근무자 원티드 요청 현황"
                  isActive={activeTab === "view"}
                  className="pt-7 pb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab("view");
                  }}
                />

                <TabButton
                  label="원티드 관리보드 설정"
                  isActive={activeTab === "settings"}
                  className="pt-7 pb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab("settings");
                  }}
                />
              </div>

              <div className="flex  shrink-0 flex-nowrap items-center gap-1 whitespace-nowrap">
                {activeTab !== "view" && (
                  <>
                    <ActionButton
                      label="근무코드 추가하기"
                      disabled={isWantedAdjustmentLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModifyModalOpen(true);
                      }}
                    />
                    <ActionButton
                      label={
                        isAllApplied ? "원티드 전체 미반영" : "원티드 전체 반영"
                      }
                      disabled={isWantedAdjustmentLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isAllApplied) {
                          handleToggleAllApplied();
                        } else {
                          handleReset();
                        }
                      }}
                    />
                    <ActionButton
                      label="저장"
                      disabled={isWantedAdjustmentLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsConfirmModalOpen(true);
                      }}
                    />
                  </>
                )}
                <ActionButton
                  label="닫기"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  ariaLabel="close"
                />
              </div>
            </div>
            <div className="w-full px-5 sticky top-[68px] justify-center bg-white  z-20 flex items-end">
              <div className=" pt-5 ">
                <div className=" flex flex-col">
                  {activeTab === "settings" && (
                    <section
                      className="mt-0 mb-4 w-full flex justify-between items-center rounded-lg border border-zinc-200 bg-[#FAF9F9] px-4 py-3"
                      aria-label="보기 설정"
                    >
                      <h2 className="text-sm font-semibold text-zinc-700">
                        보기 설정
                      </h2>
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6 sm:gap-y-3">
                        <WantedSettingSwitchRow
                          label="설정 위반사항 보기"
                          checked={localShowConfigViolations}
                          onCheckedChange={(checked) => {
                            if (checked !== localShowConfigViolations) {
                              setLocalShowConfigViolations(checked);
                            }
                          }}
                          disabled={isWantedAdjustmentLoading}
                        />
                      </div>
                    </section>
                  )}

                  <div
                    className={cn(
                      "animate-in fade-in-0 duration-300 transition-opacity relative mb-4 "
                    )}
                  >
                    <RosterMadeTable
                      key={`wanted-settings-${currentScheduleInfo.year}-${currentScheduleInfo.month}-${currentScheduleID}`}
                      defaultShiftCode={wantedStatsShiftCode}
                      year={currentScheduleInfo.year}
                      month={currentScheduleInfo.month}
                      showManpowerButton={showManpowerButton}
                      manPower_monthly={manPower_monthly}
                      memo={memo}
                      setMemo={setMemo}
                      isEditableMemo={isEditableMemo}
                      copyNurses={
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        wantedStatsNurses as any
                      }
                      hideWantedToggle={true}
                      hideManpowerToggle={true}
                      hasPrevTail={!!prevTailData}
                      onSummaryMinimizedChange={setIsRosterMadeSummaryMinimized}
                      hideViolationToggleRow
                      hideViolationFloating
                      shiftLookupMaps={shiftLookupMaps}
                      rawValidateList={
                        isWantedAdjustmentLoading ? null : rawWantedValidateList
                      }
                      isValidating={isWantedValidating}
                      phase={wantedValidationPhase}
                      violationState={{
                        showManpowerViolations: localShowManpowerViolations,
                        showConfigViolations: localShowConfigViolations,
                        showWantedViolations: localShowWantedViolations,
                        toggleManpowerViolations: () =>
                          setLocalShowManpowerViolations((prev) => !prev),
                        toggleConfigViolations: () =>
                          setLocalShowConfigViolations((prev) => !prev),
                        toggleWantedViolations: () =>
                          setLocalShowWantedViolations((prev) => !prev),
                      }}
                      role={
                        isAdmin ? "admin" : isHeadNurse ? "head_nurse" : "nurse"
                      }
                      showPrecepteeBadge={
                        wantedPrecepteePolicy.excludePrecepteeFromCount
                      }
                    />
                  </div>
                  <RosterViolationFloatingControls
                    rawValidateList={
                      isWantedAdjustmentLoading ? null : rawWantedValidateList
                    }
                    isValidating={isWantedValidating}
                    phase={wantedValidationPhase}
                    hideManpowerToggle
                    violationState={{
                      showManpowerViolations: localShowManpowerViolations,
                      showConfigViolations: localShowConfigViolations,
                      showWantedViolations: localShowWantedViolations,
                      toggleManpowerViolations: () =>
                        setLocalShowManpowerViolations((prev) => !prev),
                      toggleConfigViolations: () =>
                        setLocalShowConfigViolations((prev) => !prev),
                      toggleWantedViolations: () =>
                        setLocalShowWantedViolations((prev) => !prev),
                    }}
                  />
                </div>

                <div className=" flex gap-[10px]">
                  <TableHeader
                    defaultShiftCode={defaultShiftCode}
                    copyNurses={wantedCopyNurses}
                    setCopyNurses={setWantedCopyNurses}
                    currentYear={currentScheduleInfo.year}
                    currentMonth={currentScheduleInfo.month}
                    holidayDates={holidayDates ?? []}
                    prevTailData={prevTailData}
                    selectionFocusOnClick={activeTab === "view"}
                  />
                  <div className="size-10 relative flex items-end">
                    {/*통계 - 헤더*/}
                    <div className=" flex absolute -bottom-[17px] -left-[1px] bg-white rounded-full">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                              "transition-opacity hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            aria-pressed={showPerShiftStats}
                            aria-label={
                              showPerShiftStats
                                ? "개별 근무코드 통계 숨기기"
                                : "개별 근무코드 통계 보기"
                            }
                            onClick={() =>
                              setShowPerShiftStats((prev) => !prev)
                            }
                          >
                            <img
                              src="/asset/icon/config/icon_1.svg"
                              alt=""
                              aria-hidden
                              className={cn(
                                "size-7 object-contain transition-[filter,transform] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                showPerShiftStats ? "rotate-90" : "-rotate-90"
                              )}
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {showPerShiftStats
                              ? "개별 근무코드 통계 숨기기"
                              : "개별 근무코드 통계 보기"}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              {showPerShiftStats && (
                <div className="mb-2.5 animate-in fade-in duration-200 -translate-x-[0px] pr-2">
                  <RosterShiftLegendChips
                    displayShifts={wantedModalShiftLegend}
                    defaultShiftCode={defaultShiftCode}
                  />
                </div>
              )}
            </div>

            {/* body — Roster_create `relative pb-30 px-5`와 동일하게 헤더·범례(sticky px-5)와 가로 정렬 */}
            <div className="relative px-5 pb-30">
              {activeTab === "view" ? (
                <TableBody
                  mode="CREATE"
                  copyNurses={wantedCopyNurses}
                  setCopyNurses={setWantedCopyNurses}
                  availableShifts={shiftInfo}
                  isEditMode={false} // 읽기 전용
                  selectedShift={null}
                  modDayShift={() => {}} // 읽기 전용
                  saveNurseSequence={async () => {}} // 읽기 전용
                  currentScheduleInfo={currentScheduleInfo}
                  currentScheduleID={currentScheduleID}
                  isDragEditing={false} // 읽기 전용
                  selectedGroupId={selectedGroupId}
                  manPower_monthly={manPower_monthly}
                  manPower_daily={manPower_daily}
                  enableRowDragSort={false}
                  onlyView={true}
                  year={currentScheduleInfo.year}
                  month={currentScheduleInfo.month}
                  selectIdForSideprofile={undefined}
                  disableManpowerValidation={true} // 최소인원 검증 비활성화 (원티드 요청 현황에서는 불필요)
                  compactVerticalPadding
                  prevTailData={prevTailData}
                  showPerShiftStats={showPerShiftStats}
                  violationVisibilityOverride={{
                    showConfigViolations: localShowConfigViolations,
                    showManpowerViolations: localShowManpowerViolations,
                  }}
                  selectionFocusOnClick
                  schedulePayloadYear={currentScheduleInfo.year}
                  schedulePayloadMonth={currentScheduleInfo.month}
                  currentGroupId={
                    isAdmin ? (selectedGroupId ?? null) : (loginUser?.group_id ?? null)
                  }
                />
              ) : (
                <TableBody
                  mode="CREATE"
                  copyNurses={wantedCopyNurses}
                  setCopyNurses={setWantedCopyNurses}
                  availableShifts={shiftInfo}
                  isEditMode={true} // 원티드 반영 설정에서는 편집 모드
                  selectedShift={selectedShift} // 선택된 근무코드 전달
                  compactVerticalPadding
                  selectionFocusOnClick={false}
                  modDayShift={(nurseIndex: number, scheduleDay: number) => {
                    const nurse = wantedCopyNurses[nurseIndex];

                    if (nurse) {
                      const day = scheduleDay + 1; // 0-based -> 1-based
                      const shiftId = nurse.schedule?.[scheduleDay] || null;
                      handleWantedCellClick(
                        nurse.nurse_id,
                        day,
                        shiftId || undefined
                      );
                    }
                  }}
                  saveNurseSequence={async () => {}} // 읽기 전용
                  currentScheduleInfo={currentScheduleInfo}
                  currentScheduleID={currentScheduleID}
                  isDragEditing={false} // 읽기 전용
                  selectedGroupId={selectedGroupId}
                  manPower_monthly={manPower_monthly}
                  manPower_daily={manPower_daily}
                  enableRowDragSort={false}
                  onlyView={false} // 원티드 반영 설정에서는 클릭 가능
                  year={currentScheduleInfo.year}
                  month={currentScheduleInfo.month}
                  selectIdForSideprofile={undefined}
                  disableManpowerValidation={true} // 최소인원 검증 비활성화 (원티드 요청 현황에서는 불필요)
                  prevTailData={prevTailData}
                  showPerShiftStats={showPerShiftStats}
                  violationVisibilityOverride={{
                    showConfigViolations: localShowConfigViolations,
                    showManpowerViolations: localShowManpowerViolations,
                  }}
                  schedulePayloadYear={currentScheduleInfo.year}
                  schedulePayloadMonth={currentScheduleInfo.month}
                  currentGroupId={
                    isAdmin ? (selectedGroupId ?? null) : (loginUser?.group_id ?? null)
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 설정완료 확인 모달 */}
      <CustomModal.Confirm
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          setIsConfirmModalOpen(false);
          updateWantedAdjustment();
        }}
        isPending={isUpdatingWantedAdjustment}
        title="원티드 관리보드 설정을 저장하시겠습니까?"
        pendingMsg="원티드 관리보드 설정을 저장하는 중입니다."
        confirmText="저장"
        cancelText="취소"
      />
      {/* 닫기 확인 모달 */}
      <CustomModal.Confirm
        isOpen={isCloseConfirmModalOpen}
        onClose={() => setIsCloseConfirmModalOpen(false)}
        onConfirm={() => {
          setIsCloseConfirmModalOpen(false);
          onClose();
        }}
        title="변경사항이 저장되지 않았습니다. 정말 닫으시겠습니까?"
        confirmText="닫기"
        cancelText="취소"
      />
      {isModifyModalOpen && (
        <ModalRosterModify
          modalClose={setIsModifyModalOpen}
          availableShifts={shiftInfo as unknown as type.Type_availableShifts[]}
          selectedShift={selectedShift}
          setSelectedShift={setSelectedShift}
          deleteAllShift={deleteAllShift}
          title="근무코드 추가하기"
          deleteBtnVersion
          portalZIndex={120}
        />
      )}
    </CustomModal.Confirm>
  );
}

export default ModalRosterWanted;
